import { approvedColors } from "theme/niapColors";

/**
 * Explicit mapping from backend Color values to frontend hex codes.
 * References approvedColors from the NIAP theme palette.
 */
const BACKEND_COLOR_MAP: Record<string, string> = {
  red_light: approvedColors.coral.light,
  orange_light: approvedColors.tangerine.light,
  green_light: approvedColors.lime.light,
  teal_light: approvedColors.teal.light,
  blue_light: approvedColors.azure.light,
  indigo_light: approvedColors.indigo.light,
  purple_light: approvedColors.violet.light,
  gray_light: approvedColors.slate.light,
  red_dark: approvedColors.coral.dark,
  orange_dark: approvedColors.tangerine.dark,
  green_dark: approvedColors.lime.dark,
  teal_dark: approvedColors.teal.dark,
  blue_dark: approvedColors.azure.dark,
  indigo_dark: approvedColors.indigo.dark,
  purple_dark: approvedColors.violet.dark,
  gray_dark: approvedColors.slate.dark,
};

export const COLOR_OPTIONS = Object.keys(BACKEND_COLOR_MAP) as BackendColor[];
export type BackendColor = keyof typeof BACKEND_COLOR_MAP;

const FALLBACK_COLOR = approvedColors.slate.light;

/** Maps a backend Color value (e.g. "coral_light") to its hex code. */
export function colorAdapter(color: string): string {
  return BACKEND_COLOR_MAP[color] ?? FALLBACK_COLOR;
}

// MUI Theme Mapping

const NIAP_THEME_MAP: Record<string, string> = {
  red_light: "coral.light",
  orange_light: "tangerine.light",
  green_light: "lime.light",
  teal_light: "teal.light",
  blue_light: "azure.light",
  indigo_light: "indigo.light",
  purple_light: "violet.light",
  gray_light: "slate.light",
  red_dark: "coral.dark",
  orange_dark: "tangerine.dark",
  green_dark: "lime.dark",
  teal_dark: "teal.dark",
  blue_dark: "azure.dark",
  indigo_dark: "indigo.dark",
  purple_dark: "violet.dark",
  gray_dark: "slate.dark",
};

export const NIAP_COLOR_OPTIONS = Object.keys(
  NIAP_THEME_MAP,
) as NiapBackendColor[];
export type NiapBackendColor = keyof typeof NIAP_THEME_MAP;

const NIAP_FALLBACK_COLOR = "ink.light";

/** Maps a backend Color value (e.g. "coral_light") to its hex code. */
export function niapColorAdapter(color: string): string {
  return NIAP_THEME_MAP[color] ?? NIAP_FALLBACK_COLOR;
}

export type NiapPaletteColor =
  | "coral"
  | "tangerine"
  | "lime"
  | "teal"
  | "azure"
  | "indigo"
  | "violet"
  | "slate"
  | "navy"
  | "gold"
  | "rust"
  | "forest"
  | "ink"
  | "glyph";

/** Maps a backend Color value or theme path to its MUI palette color family (e.g. "coral"). */
export function niapColorFamily(color: string): NiapPaletteColor {
  // Already a theme path (e.g. "coral.light") — extract family directly
  if (color.includes(".")) {
    return color.split(".")[0] as NiapPaletteColor;
  }
  // Backend key (e.g. "red_light") — look up in map
  const themeColor = NIAP_THEME_MAP[color];
  if (!themeColor) return "ink";
  return themeColor.split(".")[0] as NiapPaletteColor;
}
